/**
 * Postal — Canadian postal-code formatting + normalization helpers.
 *
 * Emitted storage format: `A1A 1A1` (canonical, display-ready). Accepts any
 * raw input (pasted, lowercase, no space, hyphen-separated, mixed). Strips
 * everything non-alphanumeric, uppercases, clamps to 6 chars, inserts a
 * space between chars 3 and 4 once length ≥ 4.
 *
 * This module mirrors `crewbit-mobile/utils/postal.ts` verbatim. When admin
 * gets a fix, mobile gets the same fix. Cross-repo drift is guarded by
 * Rule #33 consumer-drift grep on primitive rollouts.
 *
 * Q4 graduates the earlier one-off helper:
 * - `SetupGuide/items/BusinessProfileItem.normalizePostal` (blur-only)
 *
 * into a single source of truth.
 */

const POSTAL_CANONICAL = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;

/** Strip non-alphanumerics, uppercase. Returns a letters+digits-only string. */
export function lettersAndDigits(raw: string | null | undefined): string {
  if (!raw) return '';
  return String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Format any raw input to the canonical `A1A 1A1` shape. Partial input
 * is rendered progressively during typing:
 *   ""      → ""
 *   "m"     → "M"
 *   "m5v"   → "M5V"
 *   "m5v3"  → "M5V 3"
 *   "m5v3a" → "M5V 3A"
 *   "m5v3a8"→ "M5V 3A8"
 *
 * Input is uppercased + clamped to 6 alphanumeric chars. Invalid shapes
 * (letter where digit expected, vice-versa) pass through unchanged at the
 * format layer — `isValidPostal` is the validation gate.
 */
export function formatPostal(raw: string | null | undefined): string {
  const clean = lettersAndDigits(raw).slice(0, 6);
  if (clean.length <= 3) return clean;
  return `${clean.slice(0, 3)} ${clean.slice(3)}`;
}

/** Canonicalize for storage/emission on blur. Empty input stays empty. */
export function canonicalPostal(raw: string | null | undefined): string {
  return formatPostal(raw);
}

/** Validate: canonical `A1A 1A1` shape with alternating letters/digits. */
export function isValidPostal(raw: string | null | undefined): boolean {
  return POSTAL_CANONICAL.test(formatPostal(raw));
}
